import { CONTACTUS_API } from "../hooks/Apis";
import { ContactUsCover } from "../assets/data/Imagedata";
import {
  FaPhone,
  FaChevronRight,
} from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import React, { useState, useEffect } from "react";
import { GlobalData } from "../assets/data/GlodalData";
import { Helmet } from "react-helmet-async";
import seoData from "../assets/data/seo.json";
import { Link } from "react-router-dom";
import LogoLoader from "../components/LogoLoader";
import { Ship } from "lucide-react";

const ContactForm = () => {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [honeypot, setHoneypot] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  const subjectOptions = [
    "Shipping Agency Inquiry",
    "Customs Clearance (CHA)",
    "Cargo Handling Services",
    "Inland Transportation",
    "Project Logistics",
    "Other",
  ];

  const faqs = [
    {
      question: "Which ports do you operate in?",
      answer: "We operate in all 12 major Indian ports, primarily in West Bengal, Odisha, and Andhra Pradesh.",
    },
    {
      question: "How can I request a quote?",
      answer: "Use the form here, email us, or call our 24/7 line with vessel details and port of call.",
    },
    {
      question: "Do you handle specialized cargo?",
      answer: "Yes, we handle bulk, breakbulk, and complex project cargo with over 3 decades of experience.",
    },
  ];

  const toggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot) return;
    setLoading(true);
    try {
      const response = await fetch(CONTACTUS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (result.success) {
        alert("Message sent successfully!");
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        alert(result.error || "Failed to send message.");
      }
    } catch (error) {
      alert("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const { title, description, keywords, canonical, ogImage } = seoData.contactus;

  const contactMethods = [
    {
      icon: <HiOutlineMail className="text-xl" />,
      label: "Email",
      value: GlobalData.company.companyEmail,
      href: `mailto:${GlobalData.company.companyEmail}`,
    },
    {
      icon: <FaPhone className="text-lg" />,
      label: "24/7 Ops",
      value: GlobalData.company.companyPhone,
      href: `tel:${GlobalData.company.companyPhone}`,
    },

  ];

  if (pageLoading) return <LogoLoader isLoading={pageLoading} />;

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* Hero Section - More Compact */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center transition-transform duration-[2s]"
          style={{ backgroundImage: `url(${ContactUsCover})` }}
        />
        <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-[34px] md:text-[36px] font-TuskerGrotesk text-white mb-2 tracking-widest uppercase italic">
            CONTACT <span className="text-secondary">US</span>
          </h1>
          <div className="w-16 h-1 bg-secondary mx-auto rounded-full"></div>
        </div>
      </section>

      {/* Main Content - Tightened */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Contact Form */}
          <div className="lg:col-span-7 bg-white p-8 md:p-12 rounded-[2.5rem] border border-slate-100 shadow-xl">
            <h2 className="text-[34px] font-TuskerGrotesk text-black mb-6 uppercase tracking-wider">
              SEND A <span className="text-primary italic">MESSAGE</span>
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <input type="text" name="honeypot" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} className="hidden" />
              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-slate-800"
                    required
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-slate-800"
                  required
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-slate-800"
                  required
                />
                <div className="md:col-span-2">
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-slate-800 appearance-none"
                    required
                  >
                    <option value="">Select Service Type</option>
                    {subjectOptions.map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <textarea
                    name="message"
                    placeholder="Your Message..."
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-5 py-3 bg-slate-50 border border-slate-100 rounded-xl focus:ring-1 focus:ring-primary outline-none transition-all font-medium text-slate-800 resize-none"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 rounded-xl font-TuskerGrotesk text-xl uppercase tracking-widest transition-all ${loading ? "bg-slate-200 text-slate-400" : "bg-primary hover:bg-black text-white shadow-lg"
                  }`}
              >
                {loading ? "SENDING..." : "SUBMIT"}
              </button>
            </form>
          </div>

          {/* Sidebar - Compact */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h3 className="text-[24px] font-TuskerGrotesk text-black uppercase  tracking-widest">CONTACT INFO</h3>
              <div className="grid gap-4">
                {contactMethods.map((method, i) => (
                  <a key={i} href={method.href} className="flex items-center p-5 bg-white rounded-2xl border border-slate-100 hover:shadow-md transition-all">
                    <div className="p-3 rounded-lg bg-primary/5 text-primary mr-4">{method.icon}</div>
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400  tracking-widest">{method.label}</p>
                      <p className="text-lg font-bold text-slate-800  ">{method.value}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Compact FAQ in Sidebar */}
            <div className="bg-black p-8 rounded-[2rem] text-white">
              <h3 className="text-[18px] font-TuskerGrotesk mb-4 uppercase tracking-widest border-b border-white/10 pb-2">FAQs</h3>
              <div className="space-y-3">
                {faqs.map((faq, i) => (
                  <div key={i} className="border-b border-white/5 pb-2 last:border-0">
                    <button onClick={() => toggle(i)} className="w-full text-left text-sm font-medium text-slate-200 flex justify-between items-center py-1">
                      <span>{faq.question}</span>
                      <FaChevronRight className={`text-xs transition-transform ${openIndex === i ? "rotate-90" : ""}`} />
                    </button>
                    {openIndex === i && <p className="text-xs text-slate-400 mt-2 italic">{faq.answer}</p>}
                  </div>
                ))}
              </div>
              <Link to="/frequently-asked-questions">
                <p className="text-[16px] font-bold text-primary uppercase tracking-widest cursor-pointer hover:text-slate-600">
                  more info &rarr;
                </p>
              </Link>
            </div>


          </div>
        </div>
      </div>

      <footer className="py-12 bg-slate-50 border-t border-slate-100 text-center">
        <Ship className="text-primary/10 w-10 h-10 mx-auto mb-4" />
        <h2 className="text-[18px] font-TuskerGrotesk text-slate-300 uppercase tracking-widest leading-tight">CONNECT WITH SHIVAM SHIPPING AND LOGISTICS - SCM</h2>
      </footer>
    </div>
  );
};
export default ContactForm;
